import { Box, Flex, Text, Tooltip, useColorModeValue } from "@chakra-ui/react";
import { ArrowDownRight, ArrowUpRight, Info, Minus } from "lucide-react";
import type { MetricCard as MetricCardData } from "../../../lib/api";

/** Métricas em que subir é ruim — a cor da variação inverte. */
const LOWER_IS_BETTER = new Set([
  "avg_completion_h",
  "time_to_first_login",
  "login_locks",
  "waiting_rh_total",
  "waiting_rh_avg_h",
  "missing_tickets",
]);

const formatValue = (value: number | null, format: MetricCardData["format"]): string => {
  if (value === null || Number.isNaN(value)) return "—";
  if (format === "percent") return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
  if (format === "hours") {
    if (value < 24) return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}h`;
    return `${(value / 24).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}d`;
  }
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
};

export const MetricCard = ({ card }: { card: MetricCardData }) => {
  // Mesmo par cardBg/border usado em AddCandidateForm, EditModal, HrAuditLogPage etc.
  const cardBg = useColorModeValue("white", "#0f2040");
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.900", "white");
  const neutral = useColorModeValue("gray.400", "gray.500");
  const good = useColorModeValue("green.600", "green.300");
  const bad = useColorModeValue("red.600", "red.300");

  const v = card.variation_pct;
  const rose = v !== null && v > 0;
  const inverted = LOWER_IS_BETTER.has(card.key);
  const isGood = v === null || v === 0 ? null : inverted ? !rose : rose;
  const trendColor = isGood === null ? neutral : isGood ? good : bad;
  const TrendIcon = v === null || v === 0 ? Minus : rose ? ArrowUpRight : ArrowDownRight;

  return (
    <Box p={4} bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="lg">
      <Flex align="center" gap={1.5} mb={2}>
        <Text
          fontSize="xs"
          fontWeight={600}
          color={labelColor}
          textTransform="uppercase"
          letterSpacing="wider"
          noOfLines={1}
        >
          {card.label}
        </Text>
        {card.hint && (
          <Tooltip label={card.hint} hasArrow placement="top" fontSize="xs">
            <Box as="span" display="inline-flex" color={neutral} cursor="help">
              <Info size={12} />
            </Box>
          </Tooltip>
        )}
      </Flex>

      <Text fontSize="2xl" fontWeight={700} color={valueColor} lineHeight={1.1} letterSpacing="-0.02em">
        {formatValue(card.value, card.format)}
      </Text>

      <Flex align="center" gap={1} mt={2} color={trendColor}>
        <TrendIcon size={13} />
        <Text fontSize="xs" fontWeight={500}>
          {v === null
            ? "sem base de comparação"
            : `${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% vs. período anterior`}
        </Text>
      </Flex>
    </Box>
  );
};
